import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  createClient,
  listClientsFull,
  listCustomFieldSchemas,
  moveClient,
  updateClient,
} from "@/api/lookups";
import type { ClientResponse, ClientSummary, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderPicker } from "@/components/folder/FolderPicker";
import { MoveFolderDialog } from "@/components/folder/MoveFolderDialog";
import {
  CustomFieldsForm,
  validateCustomFields,
} from "@/components/registries/CustomFieldsForm";
import {
  DuplicateSuggestionsDialog,
  isSimilarCandidate,
  type DuplicateCandidate,
} from "@/components/registries/DuplicateSuggestionsDialog";
import { useAuthStore } from "@/stores/auth";

export interface ClientFormDialogProps {
  open: boolean;
  existing: ClientResponse | null;
  onClose: () => void;
  /**
   * Optional initial name — used by the inline-create flow in the upload
   * dialog so the operator's typed query prefills the new-client form.
   */
  initialName?: string;
  /**
   * Optional callback fired with the freshly created or updated entity.
   * The upload dialog uses this to select the new client in the combobox
   * without an extra round-trip.
   */
  onSaved?: (saved: ClientSummary) => void;
}

// ИНН intentionally omitted from the form (TM-f518b4c8). The column
// still exists in the DB / API schema, but the UI no longer collects or
// edits it — create/edit submissions leave it unchanged.
const schema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().optional(),
  aliases: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function parseAliases(raw: string | undefined): string[] {
  if (raw === undefined || raw.length === 0) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function ClientFormDialog({
  open,
  existing,
  onClose,
  initialName,
  onSaved,
}: ClientFormDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "", aliases: "" },
  });
  const [customValues, setCustomValues] = useState<Record<string, unknown>>({});
  const [cfErrors, setCfErrors] = useState<Record<string, string>>({});
  /** Folder selection for the create flow (folder-management-v3 §2.1). */
  const [folderId, setFolderId] = useState<UUID | null>(null);
  /** Admin-only «Сменить папку» dialog state (v3 §5 + §7). */
  const [moveOpen, setMoveOpen] = useState(false);
  /**
   * Pending submit captured between the duplicate-check and the
   * user's decision. `null` means "no dialog open"; a non-null value means
   * the DuplicateSuggestionsDialog is open and we're waiting on the
   * operator to either pick an existing entity or confirm create.
   */
  const [dupState, setDupState] = useState<{
    values: FormValues;
    candidates: DuplicateCandidate[];
  } | null>(null);

  const schemas = useQuery({
    queryKey: ["custom-fields", "client"],
    queryFn: () => listCustomFieldSchemas("client"),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setCfErrors({});
    setDupState(null);
    setMoveOpen(false);
    if (existing === null) {
      form.reset({
        name: initialName ?? "",
        description: "",
        aliases: "",
      });
      setCustomValues({});
      setFolderId(null);
    } else {
      form.reset({
        name: existing.name,
        description: existing.description ?? "",
        aliases: existing.aliases.join(", "),
      });
      setCustomValues(existing.custom_field_values ?? {});
      setFolderId(existing.folder_id ?? null);
    }
  }, [open, existing, form, initialName]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      // TM-f518b4c8 — `inn` is no longer collected by the form, so the
      // payload omits it entirely. On PATCH this leaves the existing
      // backend value untouched; on POST the BE defaults to NULL.
      const basePayload = {
        name: values.name,
        description: values.description === "" ? null : values.description,
        aliases: parseAliases(values.aliases),
        custom_field_values: customValues,
      };
      if (existing === null) {
        // folder-management-v3 §2.1 — serialize the picker's folder_id
        // into the create body. Non-admins are required to supply it;
        // admins may omit (BE picks active-folder).
        return createClient(
          folderId !== null
            ? { ...basePayload, folder_id: folderId }
            : basePayload,
        );
      }
      return updateClient(existing.id, basePayload);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      if (onSaved !== undefined) {
        // createClient() returns ClientSummary; updateClient() returns the full
        // ClientResponse. Both share id/name/aliases — narrow to the summary
        // shape so the inline-create caller can drop the result straight into
        // a Combobox option.
        const summary: ClientSummary = {
          id: saved.id,
          name: saved.name,
          aliases: saved.aliases,
        };
        onSaved(summary);
      }
      onClose();
    },
  });

  /** Admin-only client move (v3 §5). Symmetric with the employee path. */
  const moveMutation = useMutation({
    mutationFn: (next: UUID) => moveClient(existing!.id, next),
    onSuccess: async (saved) => {
      const prev = existing?.folder_id ?? null;
      const next = saved.folder_id ?? null;
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      if (prev !== null) {
        await queryClient.invalidateQueries({ queryKey: ["clients", prev] });
      }
      if (next !== null) {
        await queryClient.invalidateQueries({ queryKey: ["clients", next] });
      }
    },
  });

  async function onSubmit(values: FormValues) {
    const errs = validateCustomFields(schemas.data ?? [], customValues);
    setCfErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // Only run the duplicate-suggestion gate on *create*. Edits don't need it
    // because the operator is acting on a known id, and a self-match against
    // the entity being edited would be noise.
    if (existing === null) {
      const candidates = await fetchDuplicateCandidates(values.name);
      if (candidates.length > 0) {
        setDupState({ values, candidates });
        return;
      }
    }
    mutation.mutate(values);
  }

  /**
   * Hit the existing `GET /api/clients?search=` and filter the response with
   * the FE-side normalize check so the operator sees ≤3 *plausible*
   * candidates rather than every ILIKE substring hit. Failures here are
   * non-fatal: we fall through to a normal create so a flaky network never
   * blocks data entry.
   */
  async function fetchDuplicateCandidates(
    name: string,
  ): Promise<DuplicateCandidate[]> {
    try {
      const results = await listClientsFull({ search: name, limit: 10 });
      return results
        .filter((c) => isSimilarCandidate(name, c))
        .slice(0, 3)
        .map((c) => ({ id: c.id, name: c.name, aliases: c.aliases }));
    } catch {
      return [];
    }
  }

  function handlePickExisting(id: string): void {
    if (onSaved !== undefined) {
      const picked = dupState?.candidates.find((c) => c.id === id);
      if (picked !== undefined) {
        onSaved({ id: picked.id, name: picked.name, aliases: picked.aliases });
      }
    }
    setDupState(null);
    onClose();
  }

  function handleCreateAnyway(): void {
    const pending = dupState;
    setDupState(null);
    if (pending !== null) mutation.mutate(pending.values);
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {existing === null ? t("clients.createButton") : t("common.edit")}
            </DialogTitle>
          </DialogHeader>
          <form
            className="space-y-3"
            onSubmit={form.handleSubmit(onSubmit)}
            data-testid="client-form"
          >
            <div>
              <Label htmlFor="client-name">{t("clients.fields.name")}</Label>
              <Input
                id="client-name"
                {...form.register("name")}
                aria-invalid={form.formState.errors.name !== undefined}
              />
              {form.formState.errors.name !== undefined ? (
                <p className="text-xs text-destructive">
                  {t("clients.errors.nameRequired")}
                </p>
              ) : null}
            </div>
            {/* ИНН input intentionally removed from the form (TM-f518b4c8).
                The DB column and API schema are preserved; the create/edit
                UI no longer collects it. */}
            <div>
              <Label htmlFor="client-description">
                {t("clients.fields.description")}
              </Label>
              <Input
                id="client-description"
                {...form.register("description")}
              />
            </div>
            <div>
              <Label htmlFor="client-aliases">
                {t("clients.fields.aliases")}
              </Label>
              <Input id="client-aliases" {...form.register("aliases")} />
            </div>
            {existing === null ? (
              <div>
                <Label htmlFor="client-folder">
                  {t("folders.picker.label")}
                </Label>
                <FolderPicker
                  id="client-folder"
                  value={folderId}
                  onChange={setFolderId}
                />
              </div>
            ) : role === "admin" ? (
              // Edit-mode + admin: «Сменить папку» trigger. Non-admin
              // moves keep flowing through PATCH (v3 §5 / v2 §3.2).
              <div>
                <Label>{t("folders.picker.label")}</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="move-folder-button"
                    aria-label={t("folders.move.buttonAria")}
                    onClick={() => setMoveOpen(true)}
                  >
                    {t("folders.move.button")}
                  </Button>
                </div>
              </div>
            ) : null}
            <CustomFieldsForm
              schemas={schemas.data ?? []}
              values={customValues}
              onChange={setCustomValues}
              errors={cfErrors}
              idPrefix="client-cf"
            />
            {mutation.isError ? (
              <p className="text-xs text-destructive" role="alert">
                {t("clients.errors.saveFailed")}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? t("common.saving") : t("common.save")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {dupState !== null ? (
        <DuplicateSuggestionsDialog
          open
          query={dupState.values.name}
          candidates={dupState.candidates}
          onPickExisting={handlePickExisting}
          onCreateAnyway={handleCreateAnyway}
          onCancel={() => setDupState(null)}
        />
      ) : null}
      {existing !== null && role === "admin" ? (
        <MoveFolderDialog
          open={moveOpen}
          currentFolderId={existing.folder_id ?? null}
          onMove={(next) =>
            moveMutation.mutateAsync(next).then(() => undefined)
          }
          onClose={() => setMoveOpen(false)}
        />
      ) : null}
    </>
  );
}
